import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const WebsiteFunctionalityPresentation = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (index) => {
    setExpandedSection(expandedSection === index ? null : index);
  };

  const sections = [
    {
      icon: '📋',
      title: 'Introduction',
      content: [
        'Next-Generation Recruitment Platform',
        'Modern web application built with React',
        'AI-powered content generation',
        'Automated SEO optimization',
        'Industry-leading performance'
      ],
      details: {
        platform: [
          'Built with modern React 18',
          'Server-side rendering support',
          'Component-based architecture',
          'State management with Context API',
          'React Query for data fetching'
        ],
        ai: [
          'AI-powered blog generation',
          'Automated content scheduling',
          'SEO keyword optimization',
          'Content quality analysis',
          'Readability scoring'
        ],
        performance: [
          'Lazy loading components',
          'Image optimization',
          'Code splitting',
          'Bundle size optimization',
          'Performance monitoring'
        ]
      }
    },
    {
      icon: '🏗️',
      title: 'Core Platform Architecture',
      content: [
        'React-based SPA',
        'Node.js with Firebase integration',
        'Real-time database',
        'Role-based access control',
        'Multi-language support (EN/ES)'
      ],
      details: {
        frontend: [
          'React 18 with Hooks',
          'TailwindCSS for styling',
          'Responsive design system',
          'Custom reusable components',
          'Client-side routing'
        ],
        backend: [
          'Node.js Express server',
          'Firebase for authentication',
          'Firestore database',
          'Cloud Functions integration',
          'RESTful API architecture'
        ],
        security: [
          'JWT authentication',
          'Role-based access control',
          'Firebase security rules',
          'Data encryption',
          'XSS protection'
        ]
      }
    },
    {
      icon: '🎨',
      title: 'User Interface & Experience',
      content: [
        'Responsive Design',
        'Cross-browser compatibility',
        'Dynamic layout adaptation',
        'Real-time chat support',
        'Dynamic form validation'
      ],
      details: {
        design: [
          'Mobile-first responsive design',
          'Custom Tailwind components',
          'Consistent design system',
          'Accessible UI elements',
          'Dark mode support'
        ],
        interaction: [
          'Real-time form validation',
          'Animated transitions',
          'Drag and drop interfaces',
          'Touch-friendly controls',
          'Progressive loading'
        ],
        userExperience: [
          'Intuitive navigation',
          'Guided user flows',
          'Error handling & feedback',
          'Progress indicators',
          'Smart default values'
        ]
      }
    },
    {
      icon: '🔍',
      title: 'Search & Discovery',
      content: [
        'Real-time job search',
        'Dynamic filtering',
        'Auto-complete suggestions',
        'Search result caching',
        'Category-based navigation'
      ],
      details: {
        searchFeatures: [
          'Elasticsearch integration', // Assuming Elasticsearch is used
          'Fuzzy search matching',
          'Relevance scoring',
          'Search analytics tracking',
          'Query optimization'
        ],
        filteringSystem: [
          'Multi-criteria filtering',
          'Salary range filters',
          'Location-based search',
          'Industry categories',
          'Experience level filters'
        ],
        searchOptimization: [
          'Client-side caching',
          'Infinite scroll pagination',
          'Debounced search',
          'Result prefetching',
          'Search history tracking'
        ]
      }
    },
   {
      icon: '📝',
      title: 'Application Management',
      content: [
        'Multi-step form system',
        'Document upload capability',
        'Progress auto-save',
        'Status tracking',
        'Application history'
      ],
      details: {
        applicationProcess: [
          'Multi-step application forms',
          'Conditional form fields',
          'Real-time validation',
          'Progress saving',
          'Resume parsing'
        ],
        documentHandling: [
          'Secure file uploads',
          'Automated document processing',
          'Document type validation',
          'Version control',
          'Access control'
        ],
        candidateFeatures: [
          'Application status tracking',
          'Application history',
          'Saved jobs',
          'Profile management',
          'Communication with recruiters'
        ]
      }
    },
    {
      icon: '⚡',
      title: 'Website Performance',
      content: [
        'Initial page load: < 2 seconds',
        'Subsequent navigation: < 0.5 seconds',
        'API response time: < 100ms',
        'Search results: < 300ms',
        'Mobile-optimized loading'
      ],
      details: {
        optimizationTechniques: [
          'Code splitting',
          'Lazy loading',
          'Image optimization (WebP)',
          'CDN caching',
          'Server-side rendering'
        ],
        metrics: [
          'Time to First Byte (TTFB): < 200ms',
          'First Contentful Paint (FCP): < 1s',
          'Largest Contentful Paint (LCP): < 2.5s',
          'Cumulative Layout Shift (CLS): < 0.1',
          'Total Blocking Time (TBT): < 300ms'
        ],
        monitoring: [
          'Real-time performance monitoring',
          'Automated performance testing',
          'Error tracking',
          'Alerting system',
          'Performance dashboards'
        ]
      }
    },
    {
      icon: '🔒',
      title: 'Security & Performance',
      content: [
        'Role-based access',
        'Secure data transmission',
        'Session management',
        'Real-time monitoring',
        'Automated backups'
      ],
      details: {
        securityMeasures: [
          'HTTPS encryption',
          'JWT authentication',
          'Role-based access control',
          'Input validation',
          'CSRF protection'
        ],
        dataProtection: [
          'Data encryption at rest',
          'Data encryption in transit',
          'Regular security audits',
          'Compliance with data privacy regulations',
          'Data backup and recovery'
        ],
        systemHealth: [
          'Real-time monitoring',
          'Automated backups',
          'Error tracking',
          'Performance alerts',
          'Disaster recovery plan'
        ]
      }
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          Website Functionality Overview
        </h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sections.map((section, index) => (
            <div
              key={index}
              className="bg-gray-50 rounded-lg p-6 hover:shadow-md transition-shadow duration-300"
            >
              <div
                className="flex items-center justify-between mb-4 cursor-pointer"
                onClick={() => toggleSection(index)}
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-2">{section.icon}</span>
                  <h2 className="text-xl font-semibold text-gray-800">{section.title}</h2>
                </div>
                {expandedSection === index ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </div>
              
              {/* Basic Content */}
              <ul className="space-y-2 mb-4">
                {section.content.map((item, i) => (
                  <li key={i} className="text-gray-600 flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    {item}
                  </li>
                ))}
              </ul>

              {/* Expanded Details */}
              {expandedSection === index && section.details && (
                <div className="mt-6 space-y-6 border-t pt-4">
                  {Object.entries(section.details).map(([category, items]) => (
                    <div key={category} className="space-y-2">
                      <h3 className="text-lg font-medium text-gray-700 capitalize mb-2">
                        {category}
                      </h3>
                      <ul className="space-y-2 pl-4">
                        {items.map((item, i) => (
                          <li key={i} className="text-gray-600 flex items-start">
                            <span className="text-blue-500 mr-2">→</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 text-sm text-gray-500 text-center">
          Last updated: {new Date().toLocaleDateString()}
        </div>
      </div>
    </div>
  );
};

export default WebsiteFunctionalityPresentation;